import { Frame } from "lucide-react";
import ServicePage from "./ServicePage";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../components/ui/carousel";
import { useServicePhotos } from "../hooks/useServicePhotos";

const basePhotos = [
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/fmc12avf_IMG_6954.jpeg", alt: "Binnenschilderwerk woonkamer - Schilderwerk Breukelen" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/774w7dt3_IMG_6960.jpeg", alt: "Schilderwerk hal en kozijnen - Cardoso Bouw" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/lxb1niow_0b9a96ba-fb50-4805-bb07-d74486690f1a.jpeg", alt: "Maatwerk TV meubel met sfeerhaard - Afgewerkt project" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/ml6g1bpv_5c8309d6-b276-4cba-a499-d2d8fb47c334.jpeg", alt: "Trap schilderwerk zwart - Lakwerk Cardoso Bouw" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/3uw1dn9p_IMG_6958.jpeg", alt: "Muren en kozijnen geschilderd - Schilderwerk project" },
];

const faqs = [
  {
    q: "Wanneer is het beste seizoen voor buitenschilderwerk?",
    a: "Van het voorjaar tot begin herfst, op droge dagen met temperaturen boven de 10 graden en een lage luchtvochtigheid. Binnenschilderwerk kunnen wij het hele jaar door uitvoeren.",
  },
  {
    q: "Hoe vaak moet buitenschilderwerk onderhouden worden?",
    a: "Gemiddeld elke 4 tot 6 jaar, afhankelijk van de ligging (zon- of schaduwkant), de houtsoort en het gebruikte verfsysteem. Regelmatig onderhoud voorkomt houtrot en kostbare vervangingen.",
  },
  {
    q: "Repareren jullie ook houtrot?",
    a: "Ja. Wij inspecteren al het houtwerk, verwijderen aangetast hout en herstellen dit met houtrotvuller of nieuw hout voordat wij gaan schilderen. Zo bent u verzekerd van een duurzaam resultaat.",
  },
  {
    q: "Welke verf gebruiken jullie?",
    a: "Uitsluitend hoogwaardige verfsystemen van gerenommeerde merken, afgestemd op de ondergrond en het gebruik: binnen of buiten, hout, muur of metaal. Zo blijft het resultaat jarenlang mooi.",
  },
  {
    q: "Geven jullie ook kleuradvies?",
    a: "Ja, wij adviseren graag over kleuren en afwerkingen (mat, zijdeglans of hoogglans) die passen bij de stijl van uw woning en het gebruik van de ruimte.",
  },
  {
    q: "Moet ik zelf meubels verplaatsen en afplakken?",
    a: "Nee, dat regelen wij. Wij dekken vloeren en meubels zorgvuldig af, plakken alles netjes af en laten de ruimte na afloop schoon achter.",
  },
];

function SchilderwerkCarousel() {
  const photos = useServicePhotos("schilderwerk", basePhotos);
  
  return (
    <div className="px-12" data-testid="schilderwerk-carousel">
      <Carousel opts={{ loop: true }} className="w-full">
        <CarouselContent>
          {photos.map((photo, i) => (
            <CarouselItem key={i}>
              <div className="overflow-hidden">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="bg-[#005F5F] text-white border-none hover:bg-[#C5A059] hover:text-white" />
        <CarouselNext className="bg-[#005F5F] text-white border-none hover:bg-[#C5A059] hover:text-white" />
      </Carousel>
    </div>
  );
}

export default function SchilderwerkPage() {
  return (
    <ServicePage
      seoTitle="Schilderwerk Breukelen | Binnen & Buiten - Cardoso Bouw"
      seoDescription="Professioneel schilderwerk in Breukelen voor binnen en buiten. Hoogwaardige materialen, vakkundige voorbereiding. Houtrot reparatie, lakwerk, behangwerk. Offerte aanvragen."
      seoKeywords="schilderwerk Breukelen, schilder Breukelen, binnenschilderwerk, buitenschilderwerk, houtrot reparatie, schilder Utrecht, schilder Hilversum, schilder Loosdrecht, lakwerk, schildersbedrijf"
      title="Schilderwerk"
      subtitle="Schilderwerk Breukelen"
      heroImage="https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/fmc12avf_IMG_6954.jpeg"
      icon={Frame}
      description="Professioneel schilderwerk voor binnen en buiten in Breukelen en omgeving. Bij Cardoso Bouw en Onderhoud gebruiken wij uitsluitend hoogwaardige verfproducten van gerenommeerde merken. Goed schilderwerk begint bij een gedegen voorbereiding, en daar besteden wij extra aandacht aan voor een langdurig en mooi resultaat."
      features={[
        "Binnenschilderwerk wanden en plafonds",
        "Buitenschilderwerk gevels en houtwerk",
        "Lakwerk kozijnen en deuren",
        "Behangwerk",
        "Professioneel kleuradvies",
        "Houtrot reparatie en preventie",
        "Voorbehandeling en grondwerk",
        "Schilderwerk nieuwbouw projecten",
      ]}
      faqs={faqs}
      extraContent={
        <div className="space-y-8">
          {/* PROJECT CAROUSEL */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A] mb-6">
              Onze schilderwerk projecten
            </h2>
            <SchilderwerkCarousel />
          </div>

          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A]">
            Vakkundig schilderwerk met oog voor detail
          </h2>
          <p className="text-slate-600 text-base leading-relaxed">
            Goed schilderwerk begint bij een goede voorbereiding. Onze schilders nemen de tijd 
            voor het voorbereidend werk: schuren, plamuren, gronden en waar nodig houtrot 
            repareren. Dit is essentieel voor een resultaat dat niet alleen mooi is, maar ook 
            jaren meegaat. Wij snijden geen bochten af, want kwaliteit staat bij ons voorop.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Binnenschilderwerk
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Voor het binnenschilderwerk werken wij met de beste latex en muurverven. Of u nu 
            een accent muur wilt in een opvallende kleur of uw hele interieur een frisse 
            uitstraling wilt geven, wij zorgen voor een strak en professioneel resultaat. 
            Wij schilderen muren, plafonds, kozijnen, deuren en radiatoren.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Buitenschilderwerk
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Buitenschilderwerk is niet alleen voor de uitstraling, maar beschermt uw woning 
            ook tegen de elementen. Wij inspecteren al het houtwerk op houtrot en repareren 
            dit waar nodig voordat wij beginnen met schilderen. Zo voorkomt u kostbare 
            vervangingen in de toekomst. Wij gebruiken weerbestendige verfsystemen die 
            bestand zijn tegen regen, zon en vorst.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Schilder in uw regio
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Wij voeren schilderwerk uit in heel Noord-Holland, Zuid-Holland, Utrecht en Flevoland. 
            Wij komen graag bij u langs voor een vrijblijvende offerte. Of het nu gaat om een 
            enkel kozijn of uw hele woning, wij leveren altijd kwaliteit. Ontdek het verschil 
            dat professioneel schilderwerk maakt.
          </p>
        </div>
      }
    />
  );
}
